import { createContext, useContext, useEffect, useState } from "react";
import { endpoints, AUTH_TOKEN_KEY } from "@/lib/api";

// -----------------------------------------------------------------------------
// AuthContext — thin demo-gate authentication.
//
// user state:
//   null  → still checking localStorage / validating token with /auth/me
//   false → not authenticated (no token or token expired)
//   {…}   → authenticated; holds the user object returned by the backend
// -----------------------------------------------------------------------------

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading
  const [initialising, setInitialising] = useState(true);

  // On mount, if a token exists in localStorage, validate it with /auth/me.
  // If invalid, the api interceptor will throw 401 and bounce us to /login;
  // we also clear state here as a belt-and-braces fallback.
  useEffect(() => {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!token) {
      setUser(false);
      setInitialising(false);
      return;
    }
    endpoints
      .me()
      .then((u) => setUser(u))
      .catch(() => {
        localStorage.removeItem(AUTH_TOKEN_KEY);
        setUser(false);
      })
      .finally(() => setInitialising(false));
  }, []);

  const login = async (email, password) => {
    const res = await endpoints.login(email, password);
    localStorage.setItem(AUTH_TOKEN_KEY, res.token);
    setUser(res.user);
    return res.user;
  };

  const logout = async () => {
    try { await endpoints.logout(); } catch (_) { /* stateless · ignore */ }
    localStorage.removeItem(AUTH_TOKEN_KEY);
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, initialising }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
}
